import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { addDays, fmtDate, fromISO, OVERNIGHT, RESOURCE_LABEL, toISO, todayCL } from "@/lib/format";
import { Btn, Card, PageTitle } from "@/components/admin/AdminUI";
import { StatusBadge } from "@/pages/admin/AdminReservations";

const onDay = (r, d) => (OVERNIGHT.includes(r.experience) ? r.check_in <= d && d < r.check_out : r.check_in === d);
const CHIP = { cabana: "bg-[#5E6B52] text-white", dia_privado: "bg-[#C86D51] text-white", tinaja_dia: "bg-sky-100 text-sky-800", dia_compartido: "bg-amber-100 text-amber-800", carpas: "bg-lime-100 text-lime-800", rodantes: "bg-stone-200 text-stone-800" };

function rangeFor(view, cursor) {
  const d = fromISO(cursor);
  if (view === "mes") {
    const s = new Date(d.getFullYear(), d.getMonth(), 1);
    s.setDate(s.getDate() - ((s.getDay() + 6) % 7));
    return [toISO(s), addDays(toISO(s), 41)];
  }
  if (view === "semana") {
    const s = new Date(d); s.setDate(s.getDate() - ((s.getDay() + 6) % 7));
    return [toISO(s), addDays(toISO(s), 6)];
  }
  return [cursor, cursor];
}

function DayPanel({ day, data, rs, caps, onChange }) {
  const [blk, setBlk] = useState({ resource: "todo", date_to: day, note: "" });
  const [ov, setOv] = useState({ resource: "dia_compartido", capacity: 0 });
  useEffect(() => { setBlk((b) => ({ ...b, date_to: day })); }, [day]);
  if (!data) return null;
  const u = data.usage || {};
  const block = async () => { try { await api.post("/admin/blocks", { ...blk, date_from: day }); toast.success("Fechas bloqueadas"); onChange(); } catch (e) { toast.error(errMsg(e)); } };
  const override = async () => { try { await api.post("/admin/overrides", { ...ov, date: day }); toast.success("Cupo actualizado"); onChange(); } catch (e) { toast.error(errMsg(e)); } };
  const unblock = async (id) => { await api.delete(`/admin/blocks/${id}`); onChange(); };
  const unoverride = async (resource) => { await api.delete("/admin/overrides", { params: { date: day, resource } }); toast.success("Cupo restablecido"); onChange(); };
  const cp = data.caps || caps || {};
  const isBlocked = (k) => data.blocks.some((b) => b.resource === "todo" || b.resource === k);
  const lab = (k, v) => (isBlocked(k) ? "Bloqueado" : v);
  return (
    <Card className="space-y-5" data-testid="admin-day-panel">
      <h3 className="font-serif text-2xl capitalize">{fmtDate(day, true)}</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        {[["Cabaña", lab("cabana", u.cabana ? "Ocupada" : cp.cabana ? "Libre" : "Cupo 0")], ["Habitación", lab("habitacion", u.habitacion ? "Ocupada" : cp.habitacion ? "Libre" : "Cupo 0")],
          ["Tinaja", lab("tinaja", u.tinaja ? "Ocupada" : cp.tinaja ? "Libre" : "Cupo 0")],
          ["Compartido", lab("dia_compartido", `${u.dia_compartido || 0}/${cp.dia_compartido}`)], ["Privado", lab("dia_privado", u.dia_privado ? "Reservado" : data.status.dia_privado ? "Disponible" : "No disponible")],
          ["Carpas", lab("carpas", `${u.carpas || 0}/${cp.carpas}`)], ["Casas rodantes", lab("rodantes", `${u.rodantes || 0}/${cp.rodantes}`)]].map(([k, v]) => (
          <div key={k} className="rounded-xl bg-[#FAF7F2] p-3"><p className="text-xs text-muted-foreground">{k}</p><p className="font-semibold">{v}</p></div>
        ))}
      </div>
      <div className="space-y-2">
        {rs.map((r) => <div key={r.id} className="flex flex-wrap items-center gap-2 text-sm"><span className="font-mono">{r.code}</span>{r.experience_name} · {r.customer?.nombre} · {r.guests} pers. <StatusBadge s={r.status} /></div>)}
        {!rs.length && <p className="text-sm text-muted-foreground">Sin reservas este día.</p>}
      </div>
      {(data.overrides || []).map((o) => (
        <div key={o.resource} className="flex items-center justify-between rounded-xl bg-amber-50 text-amber-800 px-3 py-2 text-sm" data-testid="override-item">
          <span>Cupo especial: {RESOURCE_LABEL[o.resource]} = {o.capacity}</span>
          <button onClick={() => unoverride(o.resource)} aria-label="Quitar cupo especial" data-testid="remove-override-btn"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      {data.blocks.map((b) => (
        <div key={b.id} className="flex items-center justify-between rounded-xl bg-red-50 text-red-800 px-3 py-2 text-sm" data-testid="block-item">
          <span>Bloqueado: {RESOURCE_LABEL[b.resource]} ({fmtDate(b.date_from)} – {fmtDate(b.date_to)}) {b.note}</span>
          <button onClick={() => unblock(b.id)} aria-label="Quitar bloqueo" data-testid="remove-block-btn"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl border border-border p-4 space-y-2">
          <p className="font-semibold text-sm">Bloquear fechas</p>
          <select value={blk.resource} onChange={(e) => setBlk({ ...blk, resource: e.target.value })} className="h-10 w-full rounded-md border px-3 text-sm bg-white" data-testid="block-resource-select">
            {Object.entries(RESOURCE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <Label className="text-xs">Hasta</Label>
          <Input type="date" value={blk.date_to} min={day} onChange={(e) => setBlk({ ...blk, date_to: e.target.value })} data-testid="block-to-input" />
          <Input placeholder="Nota" value={blk.note} onChange={(e) => setBlk({ ...blk, note: e.target.value })} />
          <Btn variant="danger" onClick={block} data-testid="create-block-btn">Bloquear</Btn>
        </div>
        <div className="rounded-xl border border-border p-4 space-y-2">
          <p className="font-semibold text-sm">Cambiar cupo de este día</p>
          <select value={ov.resource} onChange={(e) => setOv({ ...ov, resource: e.target.value })} className="h-10 w-full rounded-md border px-3 text-sm bg-white" data-testid="override-resource-select">
            {Object.entries(RESOURCE_LABEL).filter(([k]) => k !== "todo" && k !== "tinaja_dia").map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <Input type="number" min={0} value={ov.capacity} onChange={(e) => setOv({ ...ov, capacity: Number(e.target.value) })} data-testid="override-capacity-input" />
          <Btn variant="outline" onClick={override} data-testid="save-override-btn">Guardar cupo</Btn>
        </div>
      </div>
    </Card>
  );
}

export default function AdminCalendar() {
  const [view, setView] = useState("mes");
  const [cursor, setCursor] = useState(todayCL());
  const [day, setDay] = useState(todayCL());
  const [data, setData] = useState(null);
  const [start, end] = rangeFor(view, cursor);
  const load = useCallback(() => { api.get("/admin/calendar", { params: { start, end } }).then((r) => setData(r.data)); }, [start, end]);
  useEffect(() => { load(); }, [load]);
  const shift = (n) => {
    const d = fromISO(cursor);
    if (view === "mes") d.setMonth(d.getMonth() + n); else d.setDate(d.getDate() + (view === "semana" ? 7 : 1) * n);
    setCursor(toISO(d)); if (view === "dia") setDay(toISO(d));
  };
  const days = data ? Object.keys(data.days).sort() : [];
  const rsOn = (d) => (data?.reservations || []).filter((r) => onDay(r, d));
  const curMonth = fromISO(cursor).getMonth();
  return (
    <div data-testid="admin-calendar-page">
      <PageTitle title="Calendario">
        <div className="flex rounded-full bg-[#F3EDE2] p-1">
          {["dia", "semana", "mes"].map((v) => <button key={v} onClick={() => { setView(v); if (v === "dia") setCursor(day); }} data-testid={`calendar-view-${v}`}
            className={`rounded-full px-4 py-1.5 text-sm capitalize ${view === v ? "bg-[#5E6B52] text-white" : ""}`}>{v === "dia" ? "Día" : v}</button>)}
        </div>
      </PageTitle>
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => shift(-1)} className="p-2 rounded-full border bg-white" data-testid="calendar-prev"><ChevronLeft className="h-4 w-4" /></button>
        <p className="font-serif text-xl capitalize" data-testid="calendar-label">{view === "mes" ? fromISO(cursor).toLocaleDateString("es-CL", { month: "long", year: "numeric" }) : view === "semana" ? `${fmtDate(start)} – ${fmtDate(end)}` : fmtDate(cursor, true)}</p>
        <button onClick={() => shift(1)} className="p-2 rounded-full border bg-white" data-testid="calendar-next"><ChevronRight className="h-4 w-4" /></button>
      </div>
      {view !== "dia" && data && (
        <div className={`grid grid-cols-7 gap-1 mb-6 ${view === "semana" ? "" : "text-xs"}`} data-testid="calendar-grid">
          {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((d) => <p key={d} className="text-xs text-muted-foreground text-center py-1">{d}</p>)}
          {days.map((d) => {
            const rs = rsOn(d);
            const blocked = data.days[d].blocks.length > 0;
            return (
              <button key={d} onClick={() => setDay(d)} data-testid={`calendar-day-${d}`}
                className={`text-left rounded-xl border p-1.5 sm:p-2 ${view === "semana" ? "min-h-[160px]" : "min-h-[72px] sm:min-h-[96px]"} transition-colors ${day === d ? "border-[#5E6B52] ring-2 ring-[#5E6B52]/20" : "border-border"} ${blocked ? "bg-red-50" : "bg-white"} ${view === "mes" && fromISO(d).getMonth() !== curMonth ? "opacity-40" : ""}`}>
                <p className="font-semibold text-xs">{fromISO(d).getDate()}</p>
                <div className="mt-1 space-y-0.5">
                  {rs.slice(0, view === "semana" ? 8 : 3).map((r) => <p key={r.id} className={`truncate rounded px-1 text-[10px] ${CHIP[r.experience]} ${r.status === "pendiente" ? "opacity-60" : ""}`}>{r.experience_name.split("·").pop().trim()}</p>)}
                  {rs.length > (view === "semana" ? 8 : 3) && <p className="text-[10px] text-muted-foreground">+{rs.length - 3}</p>}
                </div>
              </button>
            );
          })}
        </div>
      )}
      {data && <DayPanel day={view === "dia" ? cursor : day} data={data.days[view === "dia" ? cursor : day]} rs={rsOn(view === "dia" ? cursor : day)} caps={data.capacities} onChange={load} />}
      <p className="mt-4 text-xs text-muted-foreground">Las reservas pendientes aparecen atenuadas y también reservan cupo hasta que se confirmen o rechacen.</p>
    </div>
  );
}
